import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui";
import { ContentPageTheme, Form, ReviewPageConfig, ReviewPageConfigServices } from "@/services/bedrock";
import { LiquidPlaceholderGroups, groupLiquidPlaceholders } from "@/utils/liquidHelpers";
import { PreviewPlayerState } from "../components/usePreviewPlayer";
import { ReviewPageConfigForm } from "./components/ReviewPageConfigForm";
import { ReviewPagePreview } from "./components/ReviewPagePreview";
import { ReviewPagePreviewControls } from "./components/ReviewPagePreviewControls";
import { useReviewPagePreview } from "./components/useReviewPagePreview";

interface ViewProps {
  themes: ContentPageTheme[];
  forms: Form[];
  preview: PreviewPlayerState;
}

interface ContentProps extends ViewProps {
  config: ReviewPageConfig;
  updateConfig: (updates: Partial<ReviewPageConfig>) => void;
  placeholders: LiquidPlaceholderGroups;
}

function ReviewsPageContent({ config, updateConfig, themes, forms, preview, placeholders }: ContentProps) {
  const { pageUrl, isSamplePreview, ...previewProps } = useReviewPagePreview({
    config,
    themes,
    previewPlayer: preview.previewPlayer,
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:min-h-[calc(100vh-14rem)] lg:min-w-0">
      <div className="@container p-1 flex flex-col gap-6 min-w-0 lg:min-h-0 lg:overflow-y-auto no-scrollbar">
        <ReviewPageConfigForm
          config={config}
          updateConfig={updateConfig}
          themes={themes}
          forms={forms}
          placeholders={placeholders}
        />
        <ReviewPagePreviewControls preview={preview} pageUrl={pageUrl} isSamplePreview={isSamplePreview} />
      </div>

      <div className="p-1 min-w-0 lg:sticky lg:top-0 lg:self-start">
        <ReviewPagePreview pageUrl={pageUrl} previewDevice={preview.previewDevice} {...previewProps} />
      </div>
    </div>
  );
}

export function ReviewsPage({ themes, forms, preview }: ViewProps) {
  const [config, setConfig] = useState<ReviewPageConfig | null>(null);

  const { data: configData, isError } = useQuery({
    queryKey: ["reviewPageConfig"],
    queryFn: () => ReviewPageConfigServices.getConfig(),
  });

  const { data: liquidDrop } = useQuery({
    queryKey: ["reviewPageConfigLiquidVariables"],
    queryFn: () => ReviewPageConfigServices.getLiquidVariables(),
  });

  useEffect(() => {
    if (configData) setConfig(current => current ?? configData);
  }, [configData]);

  useEffect(() => {
    if (isError) toast.error("Error loading reviews page settings");
  }, [isError]);

  const placeholders = useMemo(() => groupLiquidPlaceholders(liquidDrop?.liquidVariables ?? []), [liquidDrop]);

  const updateConfig = (updates: Partial<ReviewPageConfig>) => {
    setConfig(current => (current ? { ...current, ...updates } : current));
  };

  if (!config) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="w-full h-10 rounded-lg" />
        <Skeleton className="w-full h-24 rounded-lg" />
        <Skeleton className="w-full h-24 rounded-lg" />
      </div>
    );
  }

  return (
    <ReviewsPageContent
      config={config}
      updateConfig={updateConfig}
      themes={themes}
      forms={forms}
      preview={preview}
      placeholders={placeholders}
    />
  );
}
